import { IColumn } from '../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { ColumnFaltkodType } from '../Components/SideMenu/Components/PeriodOchKolumner/EditColumn/Components/EkonomiskAnalysEditColumnForm';
import { StandardColumnType } from '../Components/SideMenu/Components/PeriodOchKolumner/EditColumn/Components/Queries/EkonomiskAnalysStandardColumnQuery/EkonomiskAnalysStandardColumnQuery';
import { findStandardColumnByFaltkod } from '../Components/SideMenu/Components/PeriodOchKolumner/EditColumn/Utils/EkonomiskAnalysEditColumnUtils';
import {
  ILocalAnalys,
  IFilledLocalAnalys,
} from '../Components/EkonomiskAnalysMain/EkonomiskAnalysMain';
import { ekanalys_ekanalys_columns } from '../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { IDateError } from '../../../../../../Components/DateTextEdit/DateTextEdit';
import { equals } from 'ramda';
import {
  budgetVerifikationSummationMode,
  verifikationSummationMode,
} from './SummationModeUtils';
import { DateInputType } from '../../../../../../Utils/dateUtils';
import { IEkonomiskAnalysFilledDates } from '../Components/AnalysSettingsHandler/AnalysSettingsHandler';

export const getDateInputType = (
  columns: IColumn[],
  standardColumns: StandardColumnType[] | undefined
): DateInputType => {
  return columns.reduce<DateInputType>((x, y) => {
    let type = y.data.INTERVALL_TYP;
    if (type === 0) {
      if (!y.data.FALTKOD) {
        return x;
      }
      type = getTruePeriodType(
        y.data.FALTKOD as ColumnFaltkodType,
        standardColumns
      );
    } else if (type === 1) {
      type = DateInputType.yearMonthDay;
    }
    return type ? Math.max(x, type) : x;
  }, DateInputType.year);
};

const isInterval = (
  standardColumn: StandardColumnType | undefined,
  column: ekanalys_ekanalys_columns
): boolean => {
  return isIntervalBase(standardColumn, column) && !column.RULLANDE;
};

/** Kollar om en kolumn är en intervallkolumn, men bryr sig inte om rullande */
const isIntervalBase = (
  standardColumn: StandardColumnType | undefined,
  column: ekanalys_ekanalys_columns
): boolean => {
  if (!standardColumn) {
    return false;
  }

  return Boolean(
    Boolean(
      standardColumn.FROMPER &&
        standardColumn.TOMPER &&
        column.FROM_PER_RELATIV &&
        column.TOM_PER_RELATIV
    ) || column.INTERVALL_TYP === 1
  );
};

const isFromPagInv = (
  standardColumn: StandardColumnType | undefined,
  column: ekanalys_ekanalys_columns
): boolean => {
  return (
    isFromBase(standardColumn, column) &&
    !column.RULLANDE &&
    !!column.FALTKOD?.includes('PAGINV')
  );
};

const isFromBase = (
  standardColumn: StandardColumnType | undefined,
  column: ekanalys_ekanalys_columns
): boolean => {
  if (!standardColumn) {
    return false;
  }

  return Boolean(
    Boolean(standardColumn.FROMPER && column.FROM_PER_RELATIV) &&
      column.INTERVALL_TYP !== 1
  );
};

const isTomPagInv = (
  standardColumn: StandardColumnType | undefined,
  column: ekanalys_ekanalys_columns
): boolean => {
  return (
    isTomBase(standardColumn, column) &&
    !column.RULLANDE &&
    !!column.FALTKOD?.includes('PAGINV')
  );
};

const isTomBase = (
  standardColumn: StandardColumnType | undefined,
  column: ekanalys_ekanalys_columns
): boolean => {
  if (!standardColumn) {
    return false;
  }

  return Boolean(
    Boolean(standardColumn.TOMPER && column.TOM_PER_RELATIV) &&
      column.INTERVALL_TYP !== 1
  );
};

/* Returnerar true om någon kolumn i columns är en intervall kolumn */
export const hasAnyInterval = (
  columns: IColumn[],
  standardColumns: StandardColumnType[] | undefined
): boolean => {
  const cols = columns.map(col => col.data);
  const hasInterval: boolean = cols.some(col =>
    isInterval(
      findStandardColumnByFaltkod(
        col.FALTKOD as ColumnFaltkodType,
        standardColumns
      ),
      col
    )
  );
  const hasFromPagInv: boolean = cols.some(col =>
    isFromPagInv(
      findStandardColumnByFaltkod(
        col.FALTKOD as ColumnFaltkodType,
        standardColumns
      ),
      col
    )
  );
  const hasTomPagInv: boolean = cols.some(col =>
    isTomPagInv(
      findStandardColumnByFaltkod(
        col.FALTKOD as ColumnFaltkodType,
        standardColumns
      ),
      col
    )
  );

  return hasInterval || (hasFromPagInv && hasTomPagInv);
  // return cols.some(col =>
  //   isInterval(
  //     findStandardColumnByFaltkod(
  //       col.FALTKOD as ColumnFaltkodType,
  //       standardColumns
  //     ),
  //     col
  //   )
  // );
};

/** Används i EkonomiskANalysEditColumn för att bestämma om man kan sätta rullande på kolumnen
 *  Detta gör att det har lite annorlunda regler än normalt.
 */
export const columnCanBeRullande = (
  column: ekanalys_ekanalys_columns,
  standardColumns: StandardColumnType[] | undefined
): boolean => {
  return (
    isIntervalBase(
      findStandardColumnByFaltkod(
        column.FALTKOD as ColumnFaltkodType,
        standardColumns
      ),
      column
    ) && column.INTERVALL_TYP !== 1
  ); // Ska aldrig kunna sätta rullande på datum intervall
};

export const getTruePeriodType = (
  faltkod: ColumnFaltkodType,
  standardColumns: StandardColumnType[] | undefined
): DateInputType => {
  const selectedColumnOrigin = findStandardColumnByFaltkod(
    faltkod,
    standardColumns
  );
  const isPeriod =
    selectedColumnOrigin &&
    (selectedColumnOrigin.FROMPER || selectedColumnOrigin.TOMPER);

  return isPeriod ? DateInputType.yearMonth : DateInputType.year;
};

export const emptyLocalAnalys = (): ILocalAnalys => {
  return {
    loading: false,
    name: '',
    type: 'RAPPORTDIMENSIONLOPANDE',
    sums: [],
    columns: [],
    dates: {
      from: { year: 2019, month: 1, day: 1, userString: '' },
      to: { year: 2019, month: 1, day: 1, userString: '' },
    },
    urval: null,
    other: {
      budgetSummationMode: budgetVerifikationSummationMode,
      includeEjBokfordaJournaler: false,
      denomination: 1,
      decimals: 0,
      showComparison: false,
      summationMode: verifikationSummationMode,
      exkluderaPersonalKostnader: false,
      expandedSumIndex: 0,
    },
    defaultSettings: {
      budgetSummationMode: budgetVerifikationSummationMode,
      decimals: 0,
      denomination: 1,
      includeEjBokfordaJournaler: false,
      showComparison: false,
      summationMode: verifikationSummationMode,
      exkluderaPersonalKostnader: false,
      visaSummeringar: true,
    },
    expandToIndex: 0,
    anvandsFor: [],
    isInterval: false,
    sparatAnvandarUrval: null,
    lopsaldoDetaljrad: false,
    visaSummeringar: true,
    ai_prompt: null,
    detaljanalys: false,
  };
};

export const getDateInputTypeError = (
  error?: IDateError,
  dateInputType?: DateInputType
): IDateError | undefined => {
  if (!error || dateInputType === undefined) {
    return error;
  }

  return dateInputType < error.type ? undefined : error;
};

/**
 * Sets displayLabel = undefined on all columns of an analys
 */
const removeDisplayLabel = (analys: IFilledLocalAnalys) => ({
  ...analys,
  columns: analys.columns.map<IColumn>(column => ({
    ...column,
    displayLabel: undefined,
  })),
});

/**
 * Deep check if analysA has equal values to analysB
 * Ignores column displayLabels
 */
export const analysEquals = (
  analysA: IFilledLocalAnalys,
  analysB: IFilledLocalAnalys
): boolean => equals(removeDisplayLabel(analysA), removeDisplayLabel(analysB));

export const getComparisonNumber = (
  dates: IEkonomiskAnalysFilledDates,
  isInterval: boolean,
  dateInputType: DateInputType | undefined,
  helarsjamforelse: boolean | undefined
): number => {
  if (dateInputType === DateInputType.year) {
    return 100;
  }

  //periodiserad budget
  if (helarsjamforelse === false) {
    return 100;
  }

  if (isInterval) {
    return Math.round((Math.min(dates.to.month, 12) / 12) * 100 * 10) / 10;
  }

  return Math.round((Math.min(dates.from.month, 12) / 12) * 100 * 10) / 10;
};

export const getComparisonString = (
  dates: IEkonomiskAnalysFilledDates,
  isInterval: boolean,
  dateInputType: DateInputType | undefined,
  helarsjamforelse: boolean | undefined
): string =>
  getComparisonNumber(
    dates,
    isInterval,
    dateInputType,
    helarsjamforelse
  ).toLocaleString('sv-SE') + '%';
